import { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { CustomButton, CustomLoadingButton } from "src/design";
import { useRejectPayment } from "src/data/admin/queries/payment";
import { AdminPaymentDto } from "src/data/admin/types/payment";

interface RejectPaymentDialogProps {
  /** The payment to reject; `null` keeps the dialog closed. */
  payment: AdminPaymentDto | null;
  onClose: () => void;
  onRejected: (payment: AdminPaymentDto) => void;
  onError: (message: string) => void;
}

/**
 * Rejecting leaves the subscription untouched and frees the restaurateur to
 * declare a corrected payment. The reason is mandatory — the backend refuses a
 * blank one with a 400 — and it is shown to them, so the dialog says that too.
 */
export const RejectPaymentDialog = ({
  payment,
  onClose,
  onRejected,
  onError,
}: RejectPaymentDialogProps) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (payment) setReason("");
  }, [payment]);

  const rejectPayment = useRejectPayment({
    onSuccess: (rejected) => {
      onRejected(rejected);
      onClose();
    },
  });

  const isValid = reason.trim().length > 0;

  const handleSubmit = () => {
    if (!payment || !isValid) return;

    rejectPayment.mutate(
      { paymentId: payment.id, payload: { reason: reason.trim() } },
      {
        onError: (error) =>
          onError(
            error instanceof Error
              ? error.message
              : t("payments.admin.reject.failed"),
          ),
      },
    );
  };

  return (
    <Dialog
      open={!!payment}
      onClose={() => !rejectPayment.isPending && onClose()}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 600 }}>
        {t("payments.admin.reject.title", {
          restaurant: payment?.restaurantName ?? "",
        })}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {t("payments.admin.reject.body")}
        </DialogContentText>
        <TextField
          fullWidth
          multiline
          minRows={3}
          size="small"
          label={t("payments.admin.reject.reason")}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <CustomButton
          color="inverted"
          onClick={onClose}
          disabled={rejectPayment.isPending}
        >
          {t("payments.admin.reject.cancel")}
        </CustomButton>
        <CustomLoadingButton
          loading={rejectPayment.isPending}
          disabled={!isValid || rejectPayment.isPending}
          onSubmit={handleSubmit}
          label={t("payments.admin.reject.submit")}
          color="error"
          fullwidth={false}
        />
      </DialogActions>
    </Dialog>
  );
};
